/**
 * CTASectionBlock Component
 *
 * Custom BlockNote React component for call-to-action sections
 * optimized for conversions on marketing pages.
 *
 * Features:
 * - Small title, main title, description (no detail field - focused CTA)
 * - CTA buttons (1-2 max) with primary/secondary styles
 * - Featured image/video
 * - Background image/video with overlay
 * - 4 layout variations: split-left, split-right, centered-overlay, stacked
 *
 * Props: smallTitle, title, description, buttons[], featuredMedia, backgroundMedia, layout
 *
 * Related Files:
 * - app/components/blocknote/blocks/cta_section_component.rb (SSR)
 */

import React, { useState } from "react";
import MediaSelector from "./marketing/shared/MediaSelector.jsx";
import LayoutSelector from "./marketing/shared/LayoutSelector.jsx";
import OverlayControls from "./marketing/shared/OverlayControls.jsx";
import ButtonEditor from "./marketing/shared/ButtonEditor.jsx";

const LAYOUT_OPTIONS = [
  {
    value: "split-left",
    label: "Split Left",
    description: "Text left, image right",
  },
  {
    value: "split-right",
    label: "Split Right",
    description: "Image left, text right",
  },
  {
    value: "centered-overlay",
    label: "Centered Overlay",
    description: "Text over background",
  },
  { value: "stacked", label: "Stacked", description: "Text above image" },
];

export default function CTASectionBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    content: true,
    media: true,
    background: false,
    layout: true,
  });

  const props = block.props || {};
  const smallTitle = props.smallTitle || "";
  const title = props.title || "";
  const description = props.description || "";
  const buttons = props.buttons || [];
  const featuredMedia = props.featuredMedia || null;
  const backgroundMedia = props.backgroundMedia || null;
  const layout = props.layout || "split-left";

  const updateProp = (key, value) => {
    editor.updateBlock(block, { props: { ...props, [key]: value } });
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const renderSectionHeader = (title, sectionKey, required = false) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full p-3 bg-gray-100 hover:bg-gray-200 rounded-t"
      aria-expanded={expandedSections[sectionKey]}
      aria-controls={`section-${sectionKey}`}
    >
      <span className="font-medium">
        {title}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      <svg
        className={`w-5 h-5 transition-transform ${expandedSections[sectionKey] ? "rotate-180" : ""}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  );

  if (isEditMode) {
    return (
      <div className="cta-section-block border-2 border-red-200 rounded-lg p-4 bg-white my-4">
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <div className="flex items-center gap-2">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <span className="font-semibold">CTA Section</span>
          </div>
          <button
            type="button"
            onClick={() => setIsEditMode(false)}
            className="text-sm text-red-600 hover:text-red-800"
          >
            Preview
          </button>
        </div>

        <div className="mb-4">
          {renderSectionHeader("Content", "content", true)}
          {expandedSections.content && (
            <div
              id="section-content"
              className="border border-t-0 border-gray-200 rounded-b p-4 space-y-3"
            >
              <div>
                <label className="block text-sm font-medium mb-1">
                  Small Title{" "}
                  <span className="text-xs text-neutral-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={smallTitle}
                  onChange={(e) => updateProp("smallTitle", e.target.value)}
                  placeholder="e.g., LIMITED TIME"
                  className="form-control"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Main Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => updateProp("title", e.target.value)}
                  placeholder="Your compelling CTA headline"
                  className="form-control"
                  maxLength={200}
                  required
                />
                <div className="text-xs text-neutral-500 mt-1">
                  {title.length}/200
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => updateProp("description", e.target.value)}
                  placeholder="Persuasive supporting text"
                  className="form-control"
                  rows={3}
                  maxLength={500}
                  required
                />
                <div className="text-xs text-neutral-500 mt-1">
                  {description.length}/500
                </div>
              </div>
              <ButtonEditor
                buttons={buttons}
                onChange={(b) => updateProp("buttons", b)}
                maxButtons={2}
              />
            </div>
          )}
        </div>

        <div className="mb-4">
          {renderSectionHeader("Featured Media", "media")}
          {expandedSections.media && (
            <div
              id="section-media"
              className="border border-t-0 border-gray-200 rounded-b p-4"
            >
              <MediaSelector
                value={featuredMedia}
                onChange={(m) => updateProp("featuredMedia", m)}
                label="Featured Image or Video"
                allowVideo={true}
                allowYoutube={true}
              />
            </div>
          )}
        </div>

        <div className="mb-4">
          {renderSectionHeader("Background (Optional)", "background")}
          {expandedSections.background && (
            <div
              id="section-background"
              className="border border-t-0 border-gray-200 rounded-b p-4 space-y-4"
            >
              <MediaSelector
                value={backgroundMedia}
                onChange={(m) => updateProp("backgroundMedia", m)}
                label="Background Image or Video"
                allowVideo={true}
                allowYoutube={false}
              />
              {backgroundMedia?.url && (
                <OverlayControls
                  value={backgroundMedia.overlay || {}}
                  onChange={(o) =>
                    updateProp("backgroundMedia", {
                      ...backgroundMedia,
                      overlay: o,
                    })
                  }
                  showPreview={true}
                />
              )}
            </div>
          )}
        </div>

        <div className="mb-4">
          {renderSectionHeader("Layout", "layout")}
          {expandedSections.layout && (
            <div
              id="section-layout"
              className="border border-t-0 border-gray-200 rounded-b p-4"
            >
              <LayoutSelector
                value={layout}
                onChange={(l) => updateProp("layout", l)}
                layouts={LAYOUT_OPTIONS}
                blockType="cta"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Preview Mode
  return (
    <div className="cta-section-block border border-gray-300 rounded-lg p-6 bg-white my-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-neutral-600">
          CTA Section Preview
        </span>
        <button
          type="button"
          onClick={() => setIsEditMode(true)}
          className="text-sm text-red-600 hover:text-red-800"
        >
          Edit
        </button>
      </div>
      <div className="space-y-4">
        {smallTitle && (
          <div className="text-sm font-semibold text-red-600 uppercase">
            {smallTitle}
          </div>
        )}
        {title && <h2 className="text-3xl font-bold">{title}</h2>}
        {description && (
          <p className="text-lg text-neutral-600">{description}</p>
        )}
        {buttons.length > 0 && (
          <ButtonEditor
            buttons={buttons}
            onChange={() => {}}
            isEditMode={false}
          />
        )}
        {featuredMedia?.url && (
          <div className="mt-4">
            {featuredMedia.type === "image" ? (
              <img
                src={featuredMedia.url}
                alt={featuredMedia.alt || ""}
                className="max-w-full h-auto rounded"
              />
            ) : (
              <div className="text-sm text-neutral-500 p-4 border rounded">
                Video: {featuredMedia.url}
              </div>
            )}
          </div>
        )}
        <div className="text-xs text-neutral-500 mt-4 pt-4 border-t">
          Layout: {layout}
        </div>
      </div>
    </div>
  );
}
